// Idealized teaching shapes, not a physiological simulator or diagnostic engine.
// Coordinates: seconds relative to QRS onset, amplitude in mV.
export const P_WAVE = [[0, 0], [.02, .05], [.05, .14], [.08, .07], [.10, 0]];
const normal = (r = 1, s = -.24) => [[0, 0], [.012, -.08], [.03, r], [.05, s], [.08, 0]];
const scales = { I: .8, II: 1, III: .6, aVR: -.65, aVL: .5, aVF: .8, V7: .8, V8: .7, V9: .6 };
const lateral = ['I', 'aVL', 'V5', 'V6'];

export function interpolateShape(points, time) {
  if (time < points[0][0] || time > points[points.length - 1][0]) return 0;
  for (let i = 1; i < points.length; i += 1) {
    if (time <= points[i][0]) {
      const [x0, y0] = points[i - 1];
      const [x1, y1] = points[i];
      return y0 + (y1 - y0) * (time - x0) / (x1 - x0);
    }
  }
  return 0;
}

export function stOffset(id, lead) {
  const regions = {
    'st-anterior': { V1: .18, V2: .30, V3: .35, V4: .25 },
    'st-inferior': { II: .22, III: .32, aVF: .27, aVL: -.15, I: -.08 },
    'st-lateral': { I: .22, aVL: .25, V5: .25, V6: .20, II: -.10, III: -.10, aVF: -.10 },
    'st-posterior': { V1: -.18, V2: -.22, V3: -.18, V7: .15, V8: .15, V9: .12 },
    'st-depression': { V4: -.16, V5: -.20, V6: -.20, I: -.12, aVL: -.10 }
  };
  return regions[id]?.[lead] || 0;
}

export function ventricularShape(id, lead, ectopic = false) {
  let qrs = normal();
  let t = .25;
  const scale = scales[lead] ?? 1;
  if (lead === 'V1') { qrs = normal(.22, -.70); t = .10; }
  if (lead === 'V2') qrs = normal(.4, -.8);
  if (lead === 'V3') qrs = normal(.65, -.55);
  if (lead === 'V4') qrs = normal(1, -.3);
  if (['V5', 'V6'].includes(lead)) qrs = normal(1.05, -.12);
  if (id === 'rbbb') {
    qrs = [[0, 0], [.014, -.08], [.035, .85], [.065, -.23], [.13, -.20], [.16, 0]];
    if (['V1', 'V2', 'V3'].includes(lead)) {
      qrs = [[0, 0], [.025, .4], [.048, -.35], [.10, 1], [.16, 0]];
      t = -.3;
    }
  }
  if (id === 'lbbb') {
    qrs = [[0, 0], [.025, .08], [.07, -1], [.12, -.85], [.16, 0]];
    t = .3;
    if (lateral.includes(lead) || lead === 'V4') {
      qrs = [[0, 0], [.025, .65], [.05, 1], [.08, .75], [.12, 1], [.16, 0]];
      t = -.3;
    }
  }
  if (id === 'mobitz2' || id === 'av3' || ectopic || id === 'vt') {
    qrs = [[0, 0], [.04, .6], [.085, 1], [.14, -.6], [.18, 0]];
    t = -.3;
  }
  if (id === 'st-posterior' && ['V1', 'V2', 'V3'].includes(lead)) qrs = normal(.95, -.12);
  const end = qrs[qrs.length - 1][0];
  const offset = stOffset(id, lead);
  const scaled = qrs.map(([x, y]) => [x, y * scale]);
  // Add J-point deviation and an ST plateau without changing QRS onset.
  scaled[scaled.length - 1] = [end, offset];
  const stDuration = id === 'vt' ? .03 : .08;
  const tPeak = id === 'vt' ? .06 : .15;
  const recovery = id === 'vt' ? .32 : end + .27;
  return [...scaled, [end + stDuration, offset], [end + tPeak, t * scale + offset * .45], [recovery, 0]];
}

export function pAmplitude(lead) {
  return lead === 'aVR' ? -.65 : lead === 'V1' ? .6 : 1;
}